import { FC, ReactNode, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Icon, IconName } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";

import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.quickActions";

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #cfd9e1;
  background-color: #fff;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f5f7f9;
  }
`;

const FontAwesomeIcon = styled.i`
  font-size: 16px;
  width: 16px;
  text-align: center;
  color: #3d4f5f;
`;

interface QuickAction {
  labelKey: string;
  testId: string;
  renderIcon: () => ReactNode;
  onClick: () => void;
}

export const QuickActions: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();

  const {
    hideMemberCard,
    hideFindBestPricePage,
    hideFormularyLookup,
    hideInboxPage,
    hideFormDocumentsPage,
    formularyLookupOverrideUrl,
  } = useContext(ExperienceContext);

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const formularyUrl = formularyLookupOverrideUrl || memberEligibility?.formulary?.lookup_tool_url;

  const { columnWidth } = useResponsive({
    columnWidth: {
      desktop: 2,
      tablet: 4,
      mobile: 12,
    },
  });

  const actions: QuickAction[] = useMemo(() => {
    const items: QuickAction[] = [];

    if (!hideMemberCard) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewIdCard`,
        testId: "quick-action-id-card",
        renderIcon: () => <FontAwesomeIcon className="far fa-credit-card" aria-hidden="true" />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_id_card");
          navigate("/member-id-card");
        },
      });
    }

    if (!hideFindBestPricePage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.findBestPrice`,
        testId: "quick-action-find-best-price",
        renderIcon: () => <Icon name={"pill-bottle" as IconName} size="small" />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_find_best_price");
          navigate("/find-best-price");
        },
      });
    }

    if (!hideFormularyLookup && formularyUrl) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewFormulary`,
        testId: "quick-action-formulary",
        renderIcon: () => <Icon name={"medical" as IconName} size="small" />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_formulary");
          window.open(formularyUrl, "_blank", "noopener,noreferrer");
        },
      });
    }

    if (!hideInboxPage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewInbox`,
        testId: "quick-action-inbox",
        renderIcon: () => <FontAwesomeIcon className="fas fa-inbox" aria-hidden="true" />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_inbox");
          navigate("/inbox");
        },
      });
    }

    if (!hideFormDocumentsPage) {
      items.push({
        labelKey: `${TRANSLATION_PATH}.viewDocuments`,
        testId: "quick-action-documents",
        renderIcon: () => <Icon name={"document" as IconName} size="small" />,
        onClick: () => {
          logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_quick_action_documents");
          navigate("/forms-and-documents");
        },
      });
    }

    return items;
  }, [hideMemberCard, hideFindBestPricePage, hideFormularyLookup, hideInboxPage, hideFormDocumentsPage, formularyUrl]);

  if (actions.length === 0) {
    return null;
  }

  return (
    <Grid>
      {actions.map((action) => (
        <Grid.Span columns={columnWidth} key={action.labelKey}>
          <QuickActionButton
            type="button"
            onClick={action.onClick}
            data-testid={action.testId}
          >
            <Shelf alignment="center" spacing="small">
              {action.renderIcon()}
              <Subheading>{translate(action.labelKey)}</Subheading>
            </Shelf>
          </QuickActionButton>
        </Grid.Span>
      ))}
    </Grid>
  );
};
